import React, { useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { th } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

// ✅ Custom Date Input (พ.ศ.)
const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => {
  const displayValue = (() => {
    if (!value) return "";
    const [dd, mm, yyyy] = value.split("-");
    const thYear = parseInt(yyyy) + 543;
    return `${dd}/${mm}/${thYear}`;
  })();

  return (
    <input
      readOnly
      className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-300"
      onClick={onClick}
      ref={ref}
      value={displayValue}
      placeholder="เลือกวันเกิด"
    />
  );
});

export default function HoroscopeByAscendant() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleSubmit = async () => {
    if (!selectedDate || hour === "" || minute === "") {
      alert("กรุณาเลือกวันและเวลาเกิดให้ครบถ้วน");
      return;
    }

    const birthDate = format(selectedDate, "yyyy-MM-dd");
    const birthTime = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;

    // ✅ GTM DataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "click_submit_ascendant",
      category: "Ascendant",
      label: "Ascendant Form Submit",
      birth_date: birthDate, // เช่น: "2025-06-29"
      birth_time: birthTime, // เช่น: "15:30"
    });

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BIGC_BIGLINE_API_URL}/horoscope/get-horoscope-ascendant-sign`,
        { birthDate, birthTime },
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_BIGC_BIGLINE_API_KEY,
          },
        }
      );
      setResult(response.data);
      setModalOpen(true);

      // ✅ GTM DataLayer Event หลังได้ผลลัพธ์ลัคนา
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "click_view_ascendant",
        birth_date: birthDate, // เช่น: "2025-06-29"
        birth_time: birthTime, // เช่น: "15:30"
        ascendant: response.data?.data?.zodiac || "unknown", // เช่น: "เมษ"
      });
    } catch (error) {
      console.error("API Error:", error);
      setResult({ error: "ไม่สามารถดึงข้อมูลได้" });
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle =
    "w-[25%] h-[40px] mr-2 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-300";

  return (
    <div className="p-4 border rounded-lg max-w-md mx-auto mb-6 bg-white shadow">
      <h2 className="text-lg font-bold mb-4">🔮 ทำนายลัคนาราศี</h2>

      {/* ✅ Date Picker */}
      <div className="mb-4">
        <label className="mb-1 ml-2 font-medium w-[25%] float-left">
          วันเกิด
        </label>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          locale={th}
          dateFormat="dd-MM-yyyy"
          maxDate={new Date()}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          customInput={<CustomDateInput />}
          renderCustomHeader={({ date, changeYear, changeMonth }) => {
            const currentYear = date.getFullYear();
            const years = [];
            for (let y = 1950; y <= new Date().getFullYear(); y++) {
              years.push(y);
            }

            const months = [
              "มกราคม",
              "กุมภาพันธ์",
              "มีนาคม",
              "เมษายน",
              "พฤษภาคม",
              "มิถุนายน",
              "กรกฎาคม",
              "สิงหาคม",
              "กันยายน",
              "ตุลาคม",
              "พฤศจิกายน",
              "ธันวาคม",
            ];

            return (
              <div className="flex justify-between px-2 py-1 items-center">
                {/* Month */}
                <select
                  value={date.getMonth()}
                  onChange={({ target: { value } }) => changeMonth(value)}
                  className="px-2 py-1 border rounded"
                >
                  {months.map((month, idx) => (
                    <option key={month} value={idx}>
                      {month}
                    </option>
                  ))}
                </select>

                {/* Year */}
                <select
                  value={currentYear}
                  onChange={({ target: { value } }) => changeYear(value)}
                  className="px-2 py-1 border rounded"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year + 543}
                    </option>
                  ))}
                </select>
              </div>
            );
          }}
        />
      </div>

      {/* ✅ เวลาเกิด */}
      <div className="mb-4">
        <label className="mb-1 ml-3 font-medium w-[25%] float-left">
          เวลาเกิด
        </label>
        <div className="mb-1 font-medium">
          {/* ชั่วโมง */}
          <select
            value={hour}
            onChange={(e) => setHour(e.target.value)}
            className={inputStyle}
          >
            <option value="">ชั่วโมง</option>
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i.toString()}>
                {i.toString().padStart(2, "0")}
              </option>
            ))}
          </select>

          {/* นาที */}
          <select
            value={minute}
            onChange={(e) => setMinute(e.target.value)}
            className={inputStyle}
          >
            <option value="">นาที</option>
            {Array.from({ length: 60 }, (_, i) => (
              <option key={i} value={i.toString()}>
                {i.toString().padStart(2, "0")}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ✅ Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full p-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition"
      >
        {loading ? "⏳ กำลังทำนาย..." : "🔍 ดูผลลัคนา"}
      </button>

      {/* ✅ Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center z-50 p-2 pt-[5vh] ">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-4 relative max-h-[85vh] mb-30 border border-purple-200">
            <button
              className="absolute top-3 right-4 text-gray-500 hover:text-red-500 text-xl"
              onClick={() => setModalOpen(false)}
              aria-label="Close"
            >
              ❌
            </button>

            <div className="flex flex-col items-center text-center space-y-4">
              {loading ? (
                <p className="text-gray-500">⏳ กำลังโหลด...</p>
              ) : result?.data ? (
                <>
                  <div className="max-h-[80vh] overflow-y-auto pr-1">
                    <div className="text-3xl">🔮</div>
                    <h3 className="text-xl font-bold text-purple-700">
                      ดวงชาวลัคนาราศี {result.data.zodiac}
                    </h3>

                    <p className="whitespace-pre-wrap text-left">
                      {result.data.horoscope}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-red-500">
                  {result?.error || "ไม่พบผลลัพธ์"}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100]">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 text-purple-700 font-semibold animate-pulse">
            <span className="text-2xl">🔮</span>
            <span>ระบบกำลังทำนายดวง โปรดรอสักครู่...</span>
          </div>
        </div>
      )}
    </div>
  );
}
